import { DATE_FORMAT, LOCATIONS, USER_CONTEXT } from '../../../constants';
import { ManagementService } from '../../management/management.service';
import { VOICE_CALL } from '../constants/voice-call-constants';
import moment from 'moment-timezone';

import CallMadeIcon from '@mui/icons-material/CallMade';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import CallIcon from '@mui/icons-material/Call';

import { LOCATION_CACHE_KEY } from '../../../v2/services/constants';
import { UserStorage } from '../../../util/UserStorage';
import { Typography } from '@orijinworks/frontend-commons';
import { AccountCircle } from '@material-ui/icons';
export const checkVoiceCallFeatureFlag = (setGlobal, locations = LOCATIONS) => {
  const isLocationHasActiveVoiceCall = locations.some((location) => {
    return (location.activeFeatures || []).indexOf(ManagementService.MANAGEMENT_FEATURE_ENUM.VoiceCalls) > -1;
  });
  setGlobal({
    showVoiceCallTile: isLocationHasActiveVoiceCall,
  });
};

/**
 * Calculates the duration in minutes between two UTC dates.
 *
 * @param {string} startTime - The start time in UTC format.
 * @param {string} endTime - The end time in UTC format.
 * @returns {number|string} - The duration in minutes or 'N/A' if either startTime or endTime is missing.
 */
export const calculateDurationInMins = (startTime, endTime) => {
  let duration;
  if (startTime && endTime) {
    const start = moment.utc(startTime);
    const end = moment.utc(endTime);
    duration = moment.duration(end.diff(start)).asMinutes().toFixed(1) + ' Mins';
  } else {
    duration = 'N/A';
  }
  return duration;
};

/**
 * Formats the join time in UTC to a specific date format.
 * @param {string} datetime - The date time in UTC.
 * @returns {string} - The formatted date time or 'N/A' if datetime is falsy.
 */

export const formatDatetime = (
  datetime,
  timeZone = getSelectedLocationTimeZone(),
  format = DATE_FORMAT.MM_DD_YYYY_HH_MM_A
) => {
  let date;
  if (datetime) {
    date = moment.utc(datetime).tz(timeZone).format(format);
  } else {
    date = 'N/A';
  }
  return date;
};
export const generateStatusChangeOptions = (rowData, updateContactStatus, handleEditContactModalOpen, refreshData) => {
  const isApproved = rowData.status === VOICE_CALL.STATUS.APPROVED;
  const isRejected = rowData.status === VOICE_CALL.STATUS.REJECTED;
  const isBlocked = rowData.status === VOICE_CALL.STATUS.BLOCKED;
  const isPending = rowData.status === VOICE_CALL.STATUS.PENDING;
  const isArchived = rowData.status === VOICE_CALL.STATUS.INACTIVE;
  const options = [
    {
      title: 'Approve Request',
      id: VOICE_CALL.STATUS.APPROVED,
      onClick: async () => {
        await updateContactStatus(rowData, VOICE_CALL.STATUS.APPROVED);
        if (refreshData) {
          refreshData();
        }
      },
      disabled: isApproved || isBlocked,
    },
    {
      title: 'Reject Request',
      id: VOICE_CALL.STATUS.REJECTED,
      onClick: async () => {
        await updateContactStatus(rowData, VOICE_CALL.STATUS.REJECTED);
        if (refreshData) {
          refreshData();
        }
      },
      disabled: (isRejected || isBlocked || !isPending) && !isArchived,
    },
    {
      title: 'Archive Request',
      id: VOICE_CALL.STATUS.INACTIVE,
      onClick: async () => {
        await updateContactStatus(rowData, VOICE_CALL.STATUS.INACTIVE);
        if (refreshData) {
          refreshData();
        }
      },
      disabled: isPending || isBlocked || isRejected || isArchived,
    },
  ];
  if (!isRejected) {
    options.push(
      {
        title: isBlocked ? 'Unblock Request' : 'Block Request',
        id: VOICE_CALL.STATUS.BLOCKED,
        onClick: async () => {
          await updateContactStatus(rowData, isBlocked ? VOICE_CALL.STATUS.APPROVED : VOICE_CALL.STATUS.BLOCKED);
          if (refreshData) {
            refreshData();
          }
        },
        disabled: isPending,
      },
      {
        title: 'Edit',
        onClick: () => {
          handleEditContactModalOpen(rowData);
        },
        disabled: false, // Edit option is always enabled when status is not rejected
      }
    );
  }

  return options;
};

export const getSelectedLocationTimeZone = () => {
  const userStorage = new UserStorage(USER_CONTEXT.personId);
  let locationInformation = userStorage.getItem(LOCATION_CACHE_KEY);
  if (!locationInformation) {
    return moment.tz.guess();
  }

  locationInformation = JSON.parse(locationInformation);
  if (locationInformation && locationInformation.attributes) {
    const timeZoneAttr = locationInformation.attributes.find((attr) => attr.attributeName === 'TimeZone');
    if (timeZoneAttr) {
      return timeZoneAttr.attributeValue;
    }
  }
  return moment.tz.guess();
};

export const parseRelationshipType = (relationshipType) => {
  return relationshipType.replace(/_/g, ' ');
};

export const getFullName = (firstName, lastName) => `${firstName || ''} ${lastName || ''}`.trim();

export const formatTranscriptTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const paddedMinutes = minutes.toString().padStart(2, '0');
  const paddedSeconds = secs.toString().padStart(2, '0');
  return `${paddedMinutes}:${paddedSeconds}`;
};

export const highlightKeywords = (text, keywords, renderHighlight) => {
  if (!text || !keywords?.length) return text;
  const regex = new RegExp(`\\b(${keywords.join('|')})\\b|(?<=[/\\-])(${keywords.join('|')})(?=[/\\-])`, 'gi');

  return text.split(regex).map((part, index) => {
    const lowerCasePart = part?.toLowerCase();
    if (keywords.some((keyword) => lowerCasePart === keyword)) {
      return renderHighlight(part, index);
    }
    return part;
  });
};


export const getCallStatusComponents = (status) => {
  switch (status) {
    case VOICE_CALL.CALL_HISTORY_STATUS.COMPLETED:
      return {
        icon: <CallMadeIcon sx={{ color: 'var(--green-main)' }} />,
        text: <Typography sx={{ color: 'var(--green-main)' }} variant='h6'>COMPLETED</Typography>
      };
    case VOICE_CALL.CALL_HISTORY_STATUS.MISSED:
      return {
        icon: <PhoneMissedIcon sx={{ color: 'var(--orange-dark)' }} />,
        text: <Typography sx={{ color: 'var(--orange-dark)' }} variant='h6'>MISSED</Typography>
      };


    case VOICE_CALL.CALL_HISTORY_STATUS.DECLINED:
    case VOICE_CALL.CALL_HISTORY_STATUS.CANCELLED:
    case VOICE_CALL.CALL_HISTORY_STATUS.TERMINATED:
      return {
        icon: <DoNotDisturbAltIcon sx={{ color: 'var(--red-main)' }} />,
        text: <Typography sx={{ color: 'var(--red-main)' }} variant='h6'>{status}</Typography>
      };
    default:
      return {};
  }
};

export const getVoiceInsights = (voiceCallInsights, callState) => {

  if (
    !voiceCallInsights ||
    voiceCallInsights.length === 0 ||
    callState === VOICE_CALL.CALL_HISTORY_STATUS.CANCELLED ||
    callState === VOICE_CALL.CALL_HISTORY_STATUS.MISSED
  ) {
    return []; // No insights to display
  }

  // Collect unique events using a Set for event types
  const uniqueInsights = [];
  const seenEventTypes = new Set();

  for (const event of voiceCallInsights) {
    if (!seenEventTypes.has(event.eventType)) {
      uniqueInsights.push(event);
      seenEventTypes.add(event.eventType);
    }
  }

  return uniqueInsights
    .filter(
      (event) =>
        VOICE_CALL.VOICE_INSIGHTS[event.eventType] &&
        VOICE_CALL.VOICE_INSIGHTS[event.eventType].value !==
        VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT.value
    );
}

export const getCallType = (callType) => {
  switch (callType) {
    case "VRS":
      return {
        icon: <AccountCircle sx={{ color: 'var(--navy-main)' }} size={16} />,
        text: <Typography>VRS</Typography>
      }

    case "VIDEO":
      return {
        icon: <VideoCallIcon sx={{ color: 'var(--navy-main)' }} size={16} />,
        text: <Typography>Video Call</Typography>
      }

    case "PSTN":
      return {
        icon: <CallIcon sx={{ color: 'var(--navy-main)' }} size={16} />,
        text: <Typography>PSTN</Typography>
      }
  }
};